import { Routes } from '@angular/router';

import {
  anonymousGuard,
  authChildGuard,
  authGuard,
} from './core/auth/auth.guard';
import {
  anyPermissionGuard,
  permissionGuard,
} from './core/auth/permission.guard';
import { anyModuleGuard, moduleGuard } from './core/runtime/module.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [anonymousGuard],
    loadComponent: () =>
      import('./features/auth/pages/login/login').then((m) => m.Login),
  },
  {
    path: 'redefinir-senha',
    canActivate: [anonymousGuard],
    loadComponent: () =>
      import('./features/auth/pages/login/login').then((m) => m.Login),
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authChildGuard],
    loadComponent: () =>
      import('./core/layout/authenticated-shell/authenticated-shell')
        .then((m) => m.AuthenticatedShell),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/dashboard/pages/dashboard/dashboard')
            .then((m) => m.Dashboard),
      },
      {
        path: 'acesso-negado',
        loadComponent: () =>
          import('./features/auth/pages/access-denied/access-denied')
            .then((m) => m.AccessDenied),
      },
      {
        path: 'modulo-indisponivel',
        loadComponent: () =>
          import('./features/foundation/pages/module-unavailable/module-unavailable')
            .then((m) => m.ModuleUnavailable),
      },
      {
        path: 'assinador',
        loadComponent: () =>
          import('./features/documentos/pages/signer-atualizacao/signer-atualizacao')
            .then((m) => m.SignerAtualizacaoPage),
      },
      {
        path: 'minha-conta',
        loadComponent: () =>
          import('./features/account/pages/my-account/my-account')
            .then((m) => m.MyAccount),
      },
      {
        path: 'notificacoes',
        loadComponent: () =>
          import('./features/notificacoes/pages/notificacoes-page/notificacoes-page')
            .then((m) => m.NotificacoesPage),
      },
      {
        path: 'minha-equipe',
        canActivate: [
          moduleGuard('EQUIPES'),
          anyPermissionGuard(['EQUIPE_CONSULTAR', 'EQUIPE_SUPERVISIONAR']),
        ],
        loadComponent: () =>
          import('./features/equipes/pages/equipe-supervisao/equipe-supervisao')
            .then((m) => m.EquipeSupervisaoPage),
      },
      {
        path: 'escritorios-terceirizados',
        canActivate: [
          moduleGuard('ESCRITORIOS_TERCEIRIZADOS'),
          permissionGuard('ESCRITORIO_CARTEIRA_CONSULTAR'),
        ],
        loadComponent: () =>
          import('./features/escritorios/pages/carteira-escritorios/carteira-escritorios')
            .then((m) => m.CarteiraEscritoriosPage),
      },
      {
        path: 'medicoes-escritorios',
        canActivate: [
          moduleGuard('ESCRITORIOS_TERCEIRIZADOS'),
          anyPermissionGuard(['ESCRITORIO_MEDICAO_CONSULTAR', 'ESCRITORIO_MEDICAO_ADMINISTRAR']),
        ],
        loadComponent: () =>
          import('./features/escritorios/pages/medicoes-escritorios/medicoes-escritorios')
            .then((m) => m.MedicoesEscritoriosPage),
      },
      {
        path: 'migracoes',
        canActivate: [permissionGuard('MIGRACAO_CONSULTAR')],
        loadComponent: () => import('./features/migracoes/migracoes').then(m => m.MigracoesPage),
      },
      {
        path: 'processos',
        canActivate: [moduleGuard('PROCESSOS')],
        loadChildren: () =>
          import('./features/processos/processos.routes')
            .then((m) => m.processosRoutes),
      },
      {
        path: 'consultivo',
        canActivate: [moduleGuard('CONSULTIVO')],
        loadChildren: () =>
          import('./features/consultivo/consultivo.routes')
            .then((m) => m.consultivoRoutes),
      },
      {
        path: 'mediacoes',
        canActivate: [moduleGuard('MEDIACAO')],
        loadChildren: () =>
          import('./features/mediacoes/mediacoes.routes')
            .then((m) => m.mediacoesRoutes),
      },
      {
        path: 'agenda',
        canActivate: [moduleGuard('AGENDA')],
        loadChildren: () =>
          import('./features/agenda/agenda.routes')
            .then((m) => m.agendaRoutes),
      },
      {
        path: 'workflows/configuracao',
        canActivate: [
          moduleGuard('WORKFLOW'),
          permissionGuard('WORKFLOW_ADMINISTRAR'),
        ],
        loadComponent: () =>
          import('./features/workflow/pages/workflow-configuracao/workflow-configuracao')
            .then((m) => m.WorkflowConfiguracaoPage),
      },
      {
        path: 'workflows',
        canActivate: [
          moduleGuard('WORKFLOW'),
          anyPermissionGuard(['WORKFLOW_CONSULTAR', 'WORKFLOW_OPERAR', 'WORKFLOW_APROVAR', 'WORKFLOW_ADMINISTRAR']),
        ],
        loadComponent: () =>
          import('./features/workflow/pages/workflow-pendencias/workflow-pendencias')
            .then((m) => m.WorkflowPendenciasPage),
      },
      {
        path: 'documentos',
        redirectTo: 'processos',
        pathMatch: 'full',
      },

      // Cadastros básicos: dados mestres reutilizados na operação jurídica.
      {
        path: 'cadastros-basicos/pessoas-entidades',
        canActivate: [
          anyModuleGuard(['PROCESSOS', 'MEDIACAO', 'CONSULTIVO']),
          anyPermissionGuard([
            'PESSOA_CONSULTAR',
            'PESSOA_CRIAR',
            'PESSOA_ALTERAR',
          ]),
        ],
        loadComponent: () =>
          import(
            './features/cadastros-basicos/pessoas-entidades/pessoas-entidades'
          ).then((m) => m.PessoasEntidadesPage),
      },
      {
        path: 'cadastros-basicos/tipos-parte',
        canActivate: [
          moduleGuard('PROCESSOS'),
          permissionGuard('PROCESSO_TIPOS_PARTE_ADMINISTRAR'),
        ],
        loadComponent: () =>
          import(
            './features/cadastros-basicos/tipos-parte/tipos-parte'
          ).then((m) => m.TiposPartePage),
      },
      {
        path: 'cadastros-basicos/juridicos',
        canActivate: [
          moduleGuard('PROCESSOS'),
          permissionGuard('PROCESSO_CADASTROS_ADMINISTRAR'),
        ],
        loadComponent: () =>
          import(
            './features/administracao/processos/cadastros/pages/cadastros-juridicos/cadastros-juridicos'
          ).then((m) => m.CadastrosJuridicosPage),
      },
      {
        path: 'cadastros-basicos/tipos-documento',
        canActivate: [
          permissionGuard('DOCUMENTO_TIPOS_ADMINISTRAR'),
        ],
        loadComponent: () =>
          import(
            './features/administracao/documentos/pages/tipos-documento/tipos-documento'
          ).then((m) => m.TiposDocumentoPage),
      },
      {
        path: 'cadastros-basicos/modelos-documento',
        canActivate: [
          permissionGuard('TEMPLATE_DOCUMENTO_CONSULTAR'),
        ],
        loadComponent: () =>
          import(
            './features/administracao/documentos/templates/documento-templates'
          ).then((m) => m.DocumentoTemplatesPage),
      },
      {
        path: 'cadastros-basicos/indices-atualizacao',
        canActivate: [
          moduleGuard('FINANCEIRO'),
          permissionGuard('INDICE_FINANCEIRO_CONSULTAR'),
        ],
        loadComponent: () =>
          import(
            './features/cadastros-basicos/indices-atualizacao/indices-atualizacao'
          ).then((m) => m.IndicesAtualizacaoPage),
      },

      // Compatibilidade com favoritos/endereço anterior.
      {
        path: 'pessoas-partes',
        redirectTo: 'cadastros-basicos/pessoas-entidades',
        pathMatch: 'full',
      },

      {
        path: 'descobertas-processuais',
        canActivate: [
          moduleGuard('PROCESSOS'),
          permissionGuard('RADAR_DESCOBERTAS_CONSULTAR'),
        ],
        loadComponent: () =>
          import('./features/radar-descobertas/radar-descobertas').then(m => m.RadarDescobertasPage),
      },
      {
        path: 'radar-processual',
        redirectTo: 'administracao/radar-processual',
        pathMatch: 'full',
      },

      {
        path: 'comunicacoes',
        canActivate: [
          moduleGuard('COMUNICACOES'),
          permissionGuard('COMUNICACAO_CONSULTAR'),
        ],
        loadComponent: () =>
          import('./features/comunicacoes/pages/comunicacoes-caixa/comunicacoes-caixa')
            .then((m) => m.ComunicacoesCaixa),
      },
      {
        path: 'domicilio-judicial',
        canActivate: [
          moduleGuard('DOMICILIO_JUDICIAL'),
          permissionGuard('DOMICILIO_CONSULTAR'),
        ],
        data: { modo: 'operacao' },
        loadComponent: () =>
          import('./features/administracao/domicilio-judicial/domicilio-judicial')
            .then((m) => m.DomicilioJudicialPage),
      },
      {
        path: 'peticionamento',
        canActivate: [
          moduleGuard('PETICIONAMENTO'),
          permissionGuard('PETICIONAMENTO_CONSULTAR'),
        ],
        loadComponent: () =>
          import('./features/peticionamento/peticionamento')
            .then((m) => m.PeticionamentoPage),
      },
      {
        path: 'financeiro',
        canActivate: [
          moduleGuard('FINANCEIRO'),
          permissionGuard('FINANCEIRO_CONSULTAR'),
        ],
        loadComponent: () =>
          import('./features/financeiro/financeiro')
            .then((m) => m.FinanceiroPage),
      },
      {
        path: 'riscos-provisoes',
        canActivate: [
          moduleGuard('RISCO_PROVISIONAMENTO'),
          anyPermissionGuard(['RISCO_CONSULTAR', 'PROVISIONAMENTO_CONSULTAR']),
        ],
        loadComponent: () =>
          import('./features/risco-provisionamento/risco-provisionamento')
            .then((m) => m.RiscoProvisionamentoPage),
      },
      {
        path: 'relatorios',
        redirectTo: 'relatorios/visao-geral',
        pathMatch: 'full',
      },
      {
        path: 'relatorios/visao-geral',
        canActivate: [moduleGuard('RELATORIOS'), permissionGuard('ANALYTICS_CONSULTAR')],
        data: { relatoriosSecao: 'visao-geral' },
        loadComponent: () => import('./features/relatorios/relatorios').then((m) => m.RelatoriosPage),
      },
      {
        path: 'relatorios/prontos',
        canActivate: [moduleGuard('RELATORIOS'), permissionGuard('ANALYTICS_CONSULTAR')],
        data: { relatoriosSecao: 'prontos' },
        loadComponent: () => import('./features/relatorios/relatorios').then((m) => m.RelatoriosPage),
      },
      {
        path: 'relatorios/meus',
        canActivate: [moduleGuard('RELATORIOS'), permissionGuard('ANALYTICS_CONSULTAR')],
        data: { relatoriosSecao: 'meus' },
        loadComponent: () => import('./features/relatorios/relatorios').then((m) => m.RelatoriosPage),
      },
      {
        path: 'relatorios/construtor',
        canActivate: [moduleGuard('RELATORIOS'), permissionGuard('ANALYTICS_CONSULTAR')],
        data: { relatoriosSecao: 'construtor' },
        loadComponent: () => import('./features/relatorios/relatorios').then((m) => m.RelatoriosPage),
      },
      {
        path: 'relatorios/agendamentos',
        canActivate: [moduleGuard('RELATORIOS'), permissionGuard('ANALYTICS_CONSULTAR')],
        data: { relatoriosSecao: 'agendamentos' },
        loadComponent: () => import('./features/relatorios/relatorios').then((m) => m.RelatoriosPage),
      },
      {
        path: 'administracao/monitoramento-processual',
        canActivate: [permissionGuard('PROCESSO_MONITORAMENTO_ADMINISTRAR')],
        loadComponent: () =>
          import('./features/administracao/monitoramento-processual/monitoramento-processual')
            .then((m) => m.MonitoramentoProcessualPage),
      },
      {
        path: 'administracao',
        canActivate: [
          anyPermissionGuard([
            'IDENTIDADE_ADMINISTRAR',
            'ACESSO_CONSULTAR',
            'AUDITORIA_CONSULTAR',
            'PLATAFORMA_CONFIGURAR',
            'INTEGRACAO_CREDENCIAL_ADMINISTRAR',
            'PROCESSO_CADASTROS_ADMINISTRAR',
            'PROCESSO_MONITORAMENTO_ADMINISTRAR',
            'RADAR_PROCESSUAL_ADMINISTRAR',
            'WORKFLOW_AUTOMACOES_ADMINISTRAR',
            'WORKFLOW_AUSENCIAS_ADMINISTRAR',
            'EQUIPE_ADMINISTRAR',
            'ESCRITORIO_ADMINISTRAR',
            'CONTRATO_ESCRITORIO_ADMINISTRAR',
            'DOMICILIO_ADMINISTRAR',
            'PETICIONAMENTO_ADMINISTRAR',
          ]),
        ],
        loadChildren: () =>
          import('./features/administracao/administracao.routes')
            .then((m) => m.administracaoRoutes),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
